import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState(false);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [menuOpen]);

  // Scroll to top function
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth' // Smooth scrolling animation
    });
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const closeMenu = () => {
    setMenuOpen(false);
  };

  // Handle navigation click - scrolls to top and closes mobile menu
  const handleNavClick = () => {
    scrollToTop();
    closeMenu();
  };

  const colors = {
    primary: '#fddb1c',
    black: '#0a0a0a',
    blackLight: '#1a1a1a',
    white: '#ffffff',
    gray: '#999999'
  };

  const styles = {
    nav: {
      background: colors.black,
      padding: '0',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 1000,
      borderBottom: `1px solid ${colors.primary}20`,
      width: '100%',
      minHeight: '80px',
      display: 'flex',
      alignItems: 'center'
    },
    spacer: {
      height: '80px',
      width: '100%'
    },
    container: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      maxWidth: '1200px',
      margin: '0 auto',
      padding: isMobile ? '0 20px' : '0 24px',
      width: '100%',
      height: '100%'
    },
    // Logo styles - WITH MARGIN TOP TO MOVE LOGO DOWN
    logo: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      textDecoration: 'none',
      zIndex: 1002,
      height: '70px',
      maxWidth: '300px',
      margin: isMobile ? '5px 0 0 0' : '10px 0 0 0', // Margin-top to move logo down
      padding: 0
    },
    logoImage: {
      height: '100%',
      width: 'auto',
      maxWidth: '280px',
      maxHeight: '70px',
      objectFit: 'contain',
      display: 'block',
      margin: 0 // Reset margin for image
    },
    // Text fallback for logo - WITH MARGIN TOP
    logoText: {
      fontSize: '32px',
      fontWeight: 800,
      color: colors.white,
      letterSpacing: '-1px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      gap: '6px',
      whiteSpace: 'nowrap',
      lineHeight: 1,
      margin: isMobile ? '5px 0 0 0' : '10px 0 0 0', // Same margin as logo
      padding: 0
    },
    logoSpan: {
      color: colors.primary,
      textTransform: 'lowercase',
      fontWeight: 400,
      fontSize: '28px',
      display: 'inline-flex',
      alignItems: 'center',
      lineHeight: 1
    },
    // Desktop Navigation - IMPROVED ALIGNMENT
    desktopNav: {
      display: isMobile ? 'none' : 'flex',
      gap: '35px',
      alignItems: 'center',
      height: '100%',
      margin: 0,
      padding: 0
    },
    navLink: {
      color: colors.white,
      fontWeight: 500,
      fontSize: '16px',
      opacity: 0.8,
      transition: 'all 0.2s ease',
      textDecoration: 'none',
      position: 'relative',
      padding: '8px 0',
      display: 'inline-flex',
      alignItems: 'center',
      lineHeight: 1,
      cursor: 'pointer'
    },
    activeIndicator: {
      position: 'absolute',
      bottom: '-2px',
      left: '0',
      width: '100%',
      height: '3px',
      background: colors.primary,
      borderRadius: '3px'
    },
    // Desktop Contact - IMPROVED ALIGNMENT
    desktopContact: {
      display: isMobile ? 'none' : 'flex',
      alignItems: 'center',
      gap: '15px',
      marginLeft: '25px',
      paddingLeft: '25px',
      borderLeft: `1px solid ${colors.primary}30`,
      height: '100%'
    },
    contactItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      color: colors.gray,
      fontSize: '14px',
      textDecoration: 'none',
      transition: 'all 0.2s ease',
      padding: '8px 12px',
      borderRadius: '20px',
      border: `1px solid transparent`,
      whiteSpace: 'nowrap',
      lineHeight: 1,
      cursor: 'pointer'
    },
    contactIcon: {
      fontSize: '14px',
      color: colors.primary,
      display: 'inline-flex',
      alignItems: 'center'
    },
    // Hamburger Menu Button - IMPROVED ALIGNMENT
    hamburger: {
      display: isMobile ? 'flex' : 'none',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      width: '45px',
      height: '45px',
      background: colors.blackLight,
      border: `1px solid ${colors.primary}30`,
      borderRadius: '10px',
      cursor: 'pointer',
      padding: 0,
      zIndex: 1002,
      position: 'relative',
      transition: 'all 0.3s ease',
      // Center the icon inside button
      margin: 0
    },
    hamburgerInner: {
      position: 'relative',
      width: '22px',
      height: '16px',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      // Center the lines
      margin: 'auto'
    },
    hamburgerLine: {
      width: '100%',
      height: '2px',
      background: colors.primary,
      borderRadius: '2px',
      transition: 'all 0.3s ease',
      // Ensure lines are centered
      margin: 0
    },
    hamburgerLineActive1: {
      transform: 'rotate(45deg) translate(5px, 5px)',
      width: '22px'
    },
    hamburgerLineActive2: {
      opacity: 0,
      transform: 'scale(0)'
    },
    hamburgerLineActive3: {
      transform: 'rotate(-45deg) translate(5px, -5px)',
      width: '22px'
    },
    // Mobile Menu Overlay
    mobileMenu: {
      position: 'fixed',
      top: '80px',
      left: 0,
      right: 0,
      bottom: 0,
      background: colors.black,
      display: isMobile ? 'flex' : 'none',
      flexDirection: 'column',
      alignItems: 'center',
      paddingTop: '30px',
      zIndex: 1001,
      opacity: menuOpen ? 1 : 0,
      visibility: menuOpen ? 'visible' : 'hidden',
      transition: 'all 0.3s ease',
      overflowY: 'auto'
    },
    mobileMenuContent: {
      width: '100%',
      maxWidth: '320px',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px'
    },
    mobileNavItems: {
      display: 'flex',
      flexDirection: 'column',
      gap: '5px',
      background: colors.blackLight,
      borderRadius: '16px',
      padding: '10px',
      border: `1px solid ${colors.primary}20`
    },
    mobileNavLink: {
      color: colors.white,
      fontWeight: 500,
      fontSize: '18px',
      textDecoration: 'none',
      padding: '15px 20px',
      borderRadius: '12px',
      transition: 'all 0.2s ease',
      display: 'block',
      textAlign: 'center',
      background: 'transparent',
      cursor: 'pointer'
    },
    mobileNavLinkActive: {
      color: colors.primary,
      background: `${colors.primary}10`
    },
    // Mobile Contact Section
    mobileContactSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      background: colors.blackLight,
      borderRadius: '16px',
      padding: '15px',
      border: `1px solid ${colors.primary}20`
    },
    mobileContactTitle: {
      color: colors.gray,
      fontSize: '14px',
      fontWeight: 600,
      marginBottom: '5px',
      paddingLeft: '5px'
    },
    mobileContactItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      color: colors.white,
      fontSize: '15px',
      textDecoration: 'none',
      padding: '12px 15px',
      borderRadius: '12px',
      background: colors.black,
      border: `1px solid ${colors.primary}15`,
      transition: 'all 0.2s ease',
      cursor: 'pointer'
    },
    mobileContactIcon: {
      fontSize: '16px',
      color: colors.primary,
      width: '24px',
      textAlign: 'center'
    },
    mobileContactText: {
      flex: 1,
      fontSize: '14px'
    }
  };

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'Rentals', path: '/prices' },
    { name: 'About', path: '/about' },
    { name: 'Contact', path: '/contact' }
  ];

  const contactInfo = [
    {
      icon: 'fa-phone-alt',
      text: '+91 86376 58382',
      href: 'tel:+918637658382',
      label: 'Call'
    },
    {
      icon: 'fa-envelope',
      text: 'sahatravels966@gmail.com',
      href: 'mailto:sahatravels966@gmail.com',
      label: 'Email'
    }
  ];

  // Debug function to check logo loading
  const handleLogoLoad = () => {
    console.log('Logo loaded successfully');
    setLogoError(false);
  };

  const handleLogoError = () => {
    console.log('Logo failed to load, showing text fallback');
    setLogoError(true);
  };

  // Handle contact link click
  const handleContactClick = (href) => {
    // For tel and mailto links, we don't scroll
    if (href.startsWith('tel:') || href.startsWith('mailto:')) {
      // Let the browser handle normally
      return;
    }
    scrollToTop();
    closeMenu();
  };

  return (
    <>
      <nav style={styles.nav}>
        <div style={styles.container}>
          {/* Logo - WITH MARGIN TOP APPLIED */}
          <Link to="/" style={styles.logo} onClick={handleNavClick}>
            {!logoError ? (
              <img 
                src="/saha-logo.png"
                alt="SAHA Travels" 
                style={styles.logoImage}
                onLoad={handleLogoLoad}
                onError={handleLogoError}
              />
            ) : (
              <div style={styles.logoText}>
                SAHA<span style={styles.logoSpan}>travels</span>
              </div>
            )}
          </Link>

          {/* Desktop Navigation */}
          <div style={styles.desktopNav}>
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  style={{
                    ...styles.navLink,
                    opacity: isActive ? 1 : 0.8,
                  }}
                  onClick={scrollToTop} // Scroll to top when clicked
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = '1';
                    e.currentTarget.style.color = colors.primary;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = isActive ? '1' : '0.8';
                    e.currentTarget.style.color = colors.white;
                  }}
                >
                  {item.name}
                  {isActive && <div style={styles.activeIndicator} />}
                </Link>
              );
            })}

            {/* Desktop Contact */}
            <div style={styles.desktopContact}>
              {contactInfo.map((item, index) => (
                <a
                  key={index}
                  href={item.href}
                  style={styles.contactItem}
                  onClick={() => handleContactClick(item.href)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = colors.primary;
                    e.currentTarget.style.borderColor = colors.primary;
                    e.currentTarget.style.background = colors.blackLight;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = colors.gray;
                    e.currentTarget.style.borderColor = 'transparent';
                    e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <i className={`fas ${item.icon}`} style={styles.contactIcon}></i>
                  <span>{item.label}</span>
                </a>
              ))}
            </div>
          </div>

          {/* Mobile Hamburger */}
          <button 
            style={styles.hamburger} 
            onClick={toggleMenu}
            aria-label="Menu"
          >
            <div style={styles.hamburgerInner}>
              <span style={{
                ...styles.hamburgerLine,
                ...(menuOpen ? styles.hamburgerLineActive1 : {})
              }}></span>
              <span style={{
                ...styles.hamburgerLine,
                ...(menuOpen ? styles.hamburgerLineActive2 : {})
              }}></span>
              <span style={{
                ...styles.hamburgerLine,
                ...(menuOpen ? styles.hamburgerLineActive3 : {})
              }}></span>
            </div>
          </button>

          {/* Mobile Menu */}
          <div style={styles.mobileMenu}>
            <div style={styles.mobileMenuContent}>
              {/* Navigation Links */}
              <div style={styles.mobileNavItems}>
                {navItems.map((item) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.name}
                      to={item.path}
                      style={{
                        ...styles.mobileNavLink,
                        ...(isActive ? styles.mobileNavLinkActive : {})
                      }}
                      onClick={handleNavClick} // Scroll to top and close menu
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </div>

              {/* Contact Section */}
              <div style={styles.mobileContactSection}>
                <div style={styles.mobileContactTitle}>CONTACT US</div>
                {contactInfo.map((item, index) => (
                  <a
                    key={index}
                    href={item.href}
                    style={styles.mobileContactItem}
                    onClick={() => {
                      closeMenu();
                      handleContactClick(item.href);
                    }}
                  >
                    <i className={`fas ${item.icon}`} style={styles.mobileContactIcon}></i>
                    <span style={styles.mobileContactText}>{item.text}</span>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </nav>
      {/* Spacer div to push content below fixed navbar */}
      <div style={styles.spacer} />
    </>
  );
};

export default Navbar;